/*
  Product state is scoped per section. EasySell keeps ownership of checkout.
  The native name="id" + input/change contract is retained from DSTRKT.
*/
(() => {
  'use strict';
  const instances = new WeakMap();

  function init(section) {
    if (instances.has(section)) return;
    const source = section.querySelector('[data-nvl-product-data]');
    if (!source) return;
    let data;
    try { data = JSON.parse(source.textContent); } catch { return; }
    const controller = new AbortController();
    const options = { signal: controller.signal };
    instances.set(section, controller);
    const form = section.querySelector('[data-nvl-product-form]');
    const variantControl = form?.querySelector('[data-nvl-variant-id]');
    const media = [...section.querySelectorAll('[data-nvl-media]')];

    function guardEasySell() {
      const button = section.querySelector('#es-form-button');
      if (!button) return;
      if (section.dataset.nvlStockAvailable === 'false') {
        if (!button.disabled) {
          button.dataset.nvlStockDisabled = 'true';
          button.disabled = true;
        }
      } else if (button.dataset.nvlStockDisabled === 'true') {
        delete button.dataset.nvlStockDisabled;
        button.disabled = false;
      }
    }
    // The app mounts after the theme. Hide its public form for unavailable variants,
    // and guard its ordinary click/submit events without calling app internals.
    const appObserver = new MutationObserver(guardEasySell);
    appObserver.observe(section, { childList: true, subtree: true });
    controller.signal.addEventListener('abort', () => appObserver.disconnect(), { once: true });
    section.addEventListener('click', (event) => {
      if (section.dataset.nvlStockAvailable === 'false' && event.target.closest('#es-form-button')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });
    section.addEventListener('submit', (event) => {
      if (section.dataset.nvlStockAvailable === 'false' && event.target.matches('#es-form')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });

    function showMedia(id) {
      if (!media.some((slide) => slide.dataset.nvlMedia === String(id))) return;
      for (const slide of media) {
        slide.hidden = slide.dataset.nvlMedia !== String(id);
        if (slide.hidden) slide.querySelectorAll('video').forEach((video) => video.pause());
      }
      section.querySelectorAll('[data-nvl-media-target]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.nvlMediaTarget === String(id)));
      });
    }

    function updateVariant(id, updateUrl = true) {
      const variant = data.variants.find((entry) => String(entry.id) === String(id));
      if (!variant) return;
      section.dataset.nvlStockAvailable = String(variant.available);
      guardEasySell();
      section.querySelectorAll('[data-nvl-price-current]').forEach((node) => { node.textContent = variant.priceText; });
      section.querySelectorAll('[data-nvl-price-compare]').forEach((node) => {
        node.textContent = variant.compareText;
        node.hidden = !(variant.compareAt > variant.price);
      });
      section.querySelectorAll('[data-nvl-jump-order]').forEach((button) => {
        button.disabled = !variant.available;
        button.textContent = variant.available ? data.labels.order : data.labels.soldOut;
      });
      const nativeSubmit = section.querySelector('[data-nvl-native-submit]');
      if (nativeSubmit) {
        nativeSubmit.disabled = !variant.available;
        nativeSubmit.textContent = variant.available ? data.labels.add : data.labels.soldOut;
      }
      const availability = section.querySelector('[data-nvl-availability]');
      const stock = availability?.querySelector('[data-nvl-stock-label]');
      if (stock) {
        let key = variant.available ? 'in_stock' : 'sold_out';
        if (variant.available && variant.inventoryManaged && variant.inventoryPolicy === 'deny' && variant.inventoryQuantity > 0) {
          key = variant.inventoryQuantity === 1 ? 'stock_one' : 'stock_count';
        }
        stock.dataset.nvlI18n = `product.${key}`;
        stock.dataset.nvlCount = String(variant.inventoryQuantity);
        const language = window.NvlLocale?.active || 'fr';
        const template = window.NvlInterfaceLocales?.[language]?.product?.[key];
        stock.textContent = typeof template === 'string' ? template.replace(/{{\s*count\s*}}/g, variant.inventoryQuantity) : data.labels.soldOut;
        availability.hidden = false;
      }
      const quantity = form?.querySelector('input[type="number"][name="quantity"]');
      if (quantity) {
        quantity.min = variant.quantityMin;
        quantity.step = variant.quantityStep;
        if (variant.quantityMax != null) quantity.max = variant.quantityMax;
        else quantity.removeAttribute('max');
        quantity.value = variant.quantityMin;
      }
      if (variant.mediaId) showMedia(variant.mediaId);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      // Native controls are authoritative. No app-specific private API is called.
      section.dispatchEvent(new CustomEvent('nvl:variant-change', { bubbles: true, detail: { variant } }));
    }

    section.addEventListener('click', (event) => {
      const target = event.target.closest('[data-nvl-media-target]');
      if (target) { showMedia(target.dataset.nvlMediaTarget); return; }
      const step = event.target.closest('[data-nvl-gallery-step]');
      if (step && media.length) {
        const current = media.findIndex((slide) => !slide.hidden);
        const next = (current + Number(step.dataset.nvlGalleryStep) + media.length) % media.length;
        showMedia(media[next].dataset.nvlMedia);
        return;
      }
      if (event.target.closest('[data-nvl-jump-order]')) {
        const order = section.querySelector('.nvl-order-intro') || section.querySelector('[data-nvl-easysell-block]') || form;
        order?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        if (order?.matches('.nvl-order-intro')) order.focus({ preventScroll: true });
      }
    }, options);

    variantControl?.addEventListener('change', () => {
      // EasySell also receives this native, bubbling change event.
      updateVariant(variantControl.value);
    }, options);

    if (form && section.dataset.checkoutMode === 'easysell') {
      form.addEventListener('submit', (event) => {
        // An accidental Enter key must not bypass the COD application.
        event.preventDefault();
        const order = section.querySelector('.nvl-order-intro') || section.querySelector('[data-nvl-easysell-block]');
        order?.scrollIntoView({ behavior: 'auto', block: 'start' });
      }, options);
    }
    updateVariant(variantControl?.value || data.selectedVariantId, false);
  }

  function initAll(root) {
    if (root.matches?.('[data-nvl-product]')) init(root);
    root.querySelectorAll('[data-nvl-product]').forEach(init);
  }
  initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-nvl-product]').forEach((section) => {
      instances.get(section)?.abort();
      instances.delete(section);
    });
  });
})();
